import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSmsAudience } from '../useSmsAudience';
import { CampaignData, CampaignCreationHookResult } from './types';
import { hasActiveFilters, isStepComplete as checkStepComplete, isStepNavigable as checkStepNavigable, saveCampaignToSupabase } from './utils';
import { toast } from '@/hooks/use-toast';
import { OPT_OUT_MESSAGE } from '@/utils/smsUtils';
import { subDays } from 'date-fns';

export const useCampaignCreation = (userRestaurants: any[] = []): CampaignCreationHookResult => {
  const [campaignData, setCampaignData] = useState<CampaignData>({
    name: '',
    description: '',
    sendNow: true,
    scheduleDate: null,
    scheduleTime: '12:00',
    scheduleType: 'now',
    audienceType: 'all',
    message: '',
    testPhoneNumber: '',
    filters: {
      // Legacy filter structure
      visitFrequency: [],
      lastVisit: [],
      orderValue: [],
      loyaltyTier: [],
      
      // New filter structure - empty by default for 'all' audience
      totalOrders: null,
      lastVisitDate: null,
      averageOrderValue: null,
      totalSpend: null
    },
    selectedRestaurants: userRestaurants?.length > 0 ? ['all'] : []
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [shouldFetchAudience, setShouldFetchAudience] = useState(false);
  const previousStepRef = useRef<string | null>(null);
  
  // Convert new filter structure to legacy format for backward compatibility
  const convertFiltersToLegacy = useCallback((filters: any) => {
    // Convert total orders to visit frequency
    const visitFrequency: string[] = [];
    if (filters.totalOrders) {
      const { lowerLimit, upperLimit } = filters.totalOrders;
      if (lowerLimit === 1 && (!upperLimit || upperLimit === 1)) {
        visitFrequency.push('onetime');
      } else if (lowerLimit && lowerLimit >= 2 && upperLimit && upperLimit <= 5) {
        visitFrequency.push('regular');
      } else if (lowerLimit && lowerLimit > 5) {
        visitFrequency.push('frequent');
      }
    }
    
    // Convert last visit date to last visit categories
    const lastVisit: string[] = [];
    if (filters.lastVisitDate) {
      const { fromDate, toDate } = filters.lastVisitDate;
      if (fromDate && toDate) {
        const today = new Date();
        const thirtyDaysAgo = subDays(today, 30);
        const ninetyDaysAgo = subDays(today, 90);
        
        if (fromDate >= thirtyDaysAgo) {
          lastVisit.push('active');
        } else if (fromDate < thirtyDaysAgo && fromDate >= ninetyDaysAgo) {
          lastVisit.push('inactive');
        } else if (fromDate < ninetyDaysAgo) {
          lastVisit.push('lapsed');
        }
      }
    }
    
    // Convert average order value to order value categories
    const orderValue: string[] = [];
    if (filters.averageOrderValue) {
      const { lowerLimit, upperLimit } = filters.averageOrderValue;
      // These are just placeholder mappings - adjust based on actual thresholds
      if (lowerLimit !== null && lowerLimit > 50) {
        orderValue.push('high');
      } else if (lowerLimit !== null && lowerLimit > 20 && (upperLimit === null || upperLimit <= 50)) {
        orderValue.push('medium');
      } else if (upperLimit !== null && upperLimit <= 20) {
        orderValue.push('low');
      }
    }
    
    return {
      visitFrequency,
      lastVisit,
      orderValue,
      // Loyalty tier is already in the right format
      loyaltyTier: filters.loyaltyTier || []
    };
  }, []);
  
  // Memoize audience filters to prevent unnecessary re-renders
  const audienceFilters = useMemo(() => {
    if (campaignData.audienceType === 'filtered') {
      return {
        audienceType: campaignData.audienceType,
        filters: {
          // Only use new filter formats
          totalOrders: campaignData.filters.totalOrders,
          lastVisitDate: campaignData.filters.lastVisitDate,
          averageOrderValue: campaignData.filters.averageOrderValue,
          totalSpend: campaignData.filters.totalSpend
        }
      };
    } else {
      return {
        audienceType: 'all',
        filters: {
          // No filters for 'all' audience type
          totalOrders: null,
          lastVisitDate: null,
          averageOrderValue: null,
          totalSpend: null
        }
      };
    }
  }, [campaignData.audienceType, campaignData.filters]);

  const { audience, audienceSize, loading: audienceLoading } = useSmsAudience(
    campaignData.selectedRestaurants,
    audienceFilters,
    shouldFetchAudience,
    userRestaurants
  );

  // Removed useEffect that was updating campaignData.audienceSize to prevent circular dependencies
  // The audienceSize is already being returned from the hook and used directly

  const updateCampaignData = useCallback((data: Partial<CampaignData>) => {
    setCampaignData(prev => {
      // Smart merge for filters to avoid overwriting existing filter values
      let newState = { ...prev, ...data };
      if (data.filters) {
        newState.filters = { ...prev.filters, ...data.filters };
      }
      
      if (data.sendNow !== undefined) {
        newState.scheduleType = data.sendNow ? 'now' : 'scheduled';
      }
      
      if (data.audienceType === 'all' && prev.audienceType !== 'all') {
        // Clear all filters when switching to 'all'
        newState.filters = {
          visitFrequency: [],
          lastVisit: [],
          orderValue: [],
          loyaltyTier: [],
          totalOrders: null,
          lastVisitDate: null,
          averageOrderValue: null,
          totalSpend: null
        };
        setShouldFetchAudience(true);
      } 
      else if (data.audienceType === 'filtered' && prev.audienceType === 'all') {
        // Set default filter values when switching to filtered
        if (!prev.filters.totalOrders && !prev.filters.lastVisitDate && 
            !prev.filters.averageOrderValue && !prev.filters.totalSpend) {
          newState.filters = {
            ...prev.filters,
            totalOrders: { lowerLimit: 1, upperLimit: null },
            lastVisitDate: { fromDate: subDays(new Date(), 28), toDate: new Date() },
            averageOrderValue: { lowerLimit: 0, upperLimit: null },
            totalSpend: { lowerLimit: 0, upperLimit: null }
          };
        }
        setShouldFetchAudience(true);
      }
      else if (
        newState.audienceType === 'filtered' && 
        data.filters
      ) {
        // Always trigger fetch when filters are updated in filtered mode
        setShouldFetchAudience(true);
      }
      
      if (data.selectedRestaurants !== undefined) {
        setShouldFetchAudience(true);
      }
      
      return newState;
    });
  }, []);

  const isStepComplete = useCallback((step: string): boolean => {
    return checkStepComplete(step, campaignData);
  }, [campaignData]);

  const isStepNavigable = useCallback((step: string): boolean => {
    return checkStepNavigable(step, campaignData);
  }, [campaignData]);

  const handleStepChange = useCallback((newStep: string) => {
    console.log('[SMS-DEBUG] Step change:', {
      from: previousStepRef.current || 'initial',
      to: newStep,
      selectedRestaurants: campaignData.selectedRestaurants,
      hasUserRestaurants: userRestaurants?.length > 0,
      userRestaurantsCount: userRestaurants?.length || 0
    });
    
    if (newStep === 'audience' && previousStepRef.current !== 'audience') {
      console.log('[SMS-DEBUG] Entering audience step, triggering audience fetch');
      setShouldFetchAudience(true);
      
      // Log the actual restaurants that will be passed
      console.log('[SMS-DEBUG] Current restaurant selection:', {
        selectedRestaurants: campaignData.selectedRestaurants,
        includesAll: campaignData.selectedRestaurants.includes('all'),
        actualRestaurantIds: campaignData.selectedRestaurants.includes('all')
          ? userRestaurants.map(r => r.id)
          : campaignData.selectedRestaurants
      });
    }
    previousStepRef.current = newStep;
  }, [campaignData.selectedRestaurants, userRestaurants]);

  const saveCampaign = async (userProfile?: any): Promise<boolean> => {
    console.log('Starting campaign save process...', campaignData);
    console.log('User profile received in saveCampaign:', userProfile);

    if (isSubmitting) {
      console.log('Already submitting, aborting duplicate save request');
      return false;
    }

    setIsSubmitting(true);

    try {
      if (!campaignData.name || campaignData.name.trim() === '') {
        toast({
          title: "Campaign name required",
          description: "Please provide a name for your campaign.",
          variant: "destructive"
        });
        return false;
      }

      if (!campaignData.message || campaignData.message.trim() === '') {
        toast({
          title: "Message content required",
          description: "Please provide content for your campaign message.",
          variant: "destructive"
        });
        return false;
      }

      if (!campaignData.selectedRestaurants || campaignData.selectedRestaurants.length === 0) {
        toast({
          title: "Restaurant selection required",
          description: "Please select at least one restaurant for your campaign.",
          variant: "destructive"
        });
        return false;
      }

      // Calculate SMS segments for the message content if not already set
      if (!campaignData.segmentsPerMessage) {
        // Use imported OPT_OUT_MESSAGE from smsUtils for consistency
        const messageWithOptOut = campaignData.message + OPT_OUT_MESSAGE;

        // Calculate segments using the same logic as CampaignReview
        const hasUnicode = Array.from(messageWithOptOut).some(char => char.charCodeAt(0) > 127);
        const charLimit = hasUnicode ? 70 : 160;
        const segments = Math.ceil(messageWithOptOut.length / charLimit) || 1;

        // Update campaign data with segments
        setCampaignData(prev => ({
          ...prev,
          segmentsPerMessage: segments
        }));

        // Also update local variable for immediate use
        campaignData.segmentsPerMessage = segments;

        console.log(`Calculated ${segments} segments for message with length ${messageWithOptOut.length} (Unicode: ${hasUnicode})`);
      }

      console.log('Campaign data validation passed, proceeding to save');

      try {
        // For filtered campaigns, pass the audience data to enable proper backend routing
        const campaignDataWithAudience = {
          ...campaignData,
          // Pass the audience data for filtered campaigns so backend can route properly
          audience_data: campaignData.audienceType === 'filtered' ? audience : null,
          // Also pass the audience size for proper billing calculation
          audienceSize: audienceSize
        };

        console.log('Campaign data with audience:', {
          audienceType: campaignDataWithAudience.audienceType,
          hasAudienceData: !!campaignDataWithAudience.audience_data,
          audienceDataLength: campaignDataWithAudience.audience_data?.length || 0,
          audienceSize: campaignDataWithAudience.audienceSize
        });

        const result = await saveCampaignToSupabase(campaignDataWithAudience, userProfile);
        console.log('Save result from saveCampaignToSupabase:', result);
        return result;
      } catch (error) {
        console.error('Error saving campaign:', error);
        toast({
          title: "Failed to save campaign",
          description: error instanceof Error ? error.message : "An unexpected error occurred",
          variant: "destructive"
        });
        return false;
      }
    } catch (error) {
      console.error('Error in saveCampaign:', error);
      toast({
        title: "Failed to save campaign",
        description: error instanceof Error ? error.message : "An unexpected error occurred",
        variant: "destructive"
        });
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset shouldFetchAudience when loading starts to prevent multiple simultaneous requests
  useEffect(() => {
    if (shouldFetchAudience && audienceLoading) {
      setShouldFetchAudience(false);
    }
  }, [shouldFetchAudience, audienceLoading]);

  return {
    campaignData,
    updateCampaignData,
    audience,
    audienceSize,
    audienceLoading,
    isSubmitting,
    isStepComplete,
    isStepNavigable,
    saveCampaign,
    handleStepChange
  };
};